import React, { useEffect, useState } from "react";
import ArrowLeft from "../../assets/taiga-uikit/icons/arrow-left-1.svg?react";
import ArrowRight from "../../assets/taiga-uikit/icons/arrow-right-1.svg?react";
import { _size } from "../../core/core";
import { Button } from "../button/button";
import { Label } from "../texts/label";
import "./Calendar.css";
import { CalendarItem } from "./CalendarItem";

interface CalendarProps {
  minDate?: Date;
  maxDate?: Date;
  currentDate?: Date;
  is_shadow?: boolean;
  is_modal?: boolean;
  onDateChange: (date: Date) => void;
  onTimeChange: (time: string) => void;
}

const Calendar: React.FC<CalendarProps> = ({
  minDate = new Date(),
  maxDate,
  currentDate = new Date(),
  is_shadow = false,
  is_modal = false,
  onDateChange,
  onTimeChange,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(currentDate);
  const [selectedTime, setSelectedTime] = useState<string>(
    `${currentDate.getHours().toString().padStart(2, "0")}:${currentDate
      .getMinutes()
      .toString()
      .padStart(2, "0")}`
  );
  const [isMobDate, setIsMobDate] = useState<boolean>(false);

  const months = [
    "Январь",
    "Февраль",
    "Март",
    "Апрель",
    "Май",
    "Июнь",
    "Июль",
    "Август",
    "Сентябрь",
    "Октябрь",
    "Ноябрь",
    "Декабрь",
  ];

  const getDaysInMonth = (month: number, year: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  const generateMatrix = (): any[][] => {
    const matrix: any[][] = [];
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = getDaysInMonth(month, year);

    const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1;
    let dayCounter = 1 - adjustedFirstDay;
    const prevMonthDays = getDaysInMonth(month - 1, year);

    for (let row = 0; row < 6; row++) {
      const rowCells: any[] = [];
      for (let col = 0; col < 7; col++) {
        const currentDay =
          dayCounter <= 0
            ? prevMonthDays + dayCounter
            : dayCounter > daysInMonth
            ? dayCounter - daysInMonth
            : dayCounter;

        const dateToCheck = new Date(year, month, currentDay);
        const isDisabled =
          (minDate && dateToCheck < minDate) ||
          (maxDate && maxDate !== null && dateToCheck > maxDate);

        rowCells.push({
          day: currentDay,
          currentMonth: dayCounter > 0 && dayCounter <= daysInMonth,
          prevMonth: dayCounter <= 0,
          nextMonth: dayCounter > daysInMonth,
          isDisabled,
        });
        dayCounter++;
      }
      matrix.push(rowCells);
    }
    return matrix;
  };

  const handleDateClick = (
    day: number,
    currentMonth: boolean,
    prevMonth: boolean,
    nextMonth: boolean,
    isDisabled: boolean
  ): void => {
    if (isDisabled) return;
    const updatedDate = new Date(selectedDate);
    if (currentMonth) {
      updatedDate.setDate(day);
    } else if (prevMonth) {
      updatedDate.setMonth(selectedDate.getMonth() - 1);
      updatedDate.setDate(day);
    } else if (nextMonth) {
      updatedDate.setMonth(selectedDate.getMonth() + 1);
      updatedDate.setDate(day);
    }
    setSelectedDate(updatedDate);
  };

  useEffect(() => {
    onDateChange(selectedDate);
  }, [selectedDate]);

  const changeMonth = (e: React.MouseEvent, delta: number): void => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + delta);
    setSelectedDate(newDate);
    e.stopPropagation();
    e.preventDefault();
  };

  const handleTimeClick = (time: string): void => {
    setSelectedTime(time);
    onTimeChange(time);
  };

  const times = Array.from(
    { length: 24 },
    (_, i) => `${i.toString().padStart(2, "0")}:00`
  );

  return (
    <div
      className={`calendar-wrapper c-shadow-${is_shadow} c-modal-${is_modal}`}
    >
      <div className="calendar-body">
        <div className={`calendar-left c-small-date-${isMobDate}`}>
          <div className="calendar-header">
            <div className="calendar-header-left">
              <Label className="c-neutral">
                {months[selectedDate.getMonth()]}
              </Label>
              <Label className="c-neutral">{selectedDate.getFullYear()}</Label>
            </div>
            <div className="calendar-header-right">
              <Button
                className="c-neutral c-button"
                onClick={(e) => changeMonth(e, -1)}
              >
                <ArrowLeft />
              </Button>
              <Button
                className="c-neutral c-button"
                onClick={(e) => changeMonth(e, 1)}
              >
                <ArrowRight />
              </Button>
            </div>
          </div>
          <table className="calendar-table">
            <tbody>
              {generateMatrix().map((week, index) => (
                <tr key={index}>
                  {week.map(
                    (
                      { day, currentMonth, prevMonth, nextMonth, isDisabled },
                      i
                    ) => (
                      <td
                        key={i}
                        onClick={() =>
                          handleDateClick(
                            day,
                            currentMonth,
                            prevMonth,
                            nextMonth,
                            isDisabled
                          )
                        }
                      >
                        <CalendarItem
                          today={
                            day === new Date().getDate() &&
                            selectedDate.getMonth() === new Date().getMonth()
                          }
                          selected={
                            day === selectedDate.getDate() && currentMonth
                          }
                          disabled={currentMonth && !isDisabled ? false : true}
                          weekend={i === 5 || i === 6}
                        >
                          {day || ""}
                        </CalendarItem>
                      </td>
                    )
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="calendar-center">
          <div className="line-up"></div>
        </div>
        <div className={`calendar-right c-small-time-${isMobDate}`}>
          <div className="calendar-right-items">
            <div className="calendar-right-scroll">
              {times.map((time) => (
                <Button
                  key={time}
                  className={` ${
                    time === selectedTime ? "c-selected-btn" : ""
                  }`}
                  size={_size.S}
                  onClick={() => handleTimeClick(time)}
                >
                  {time}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="calendar-small">
        <Button
          className={`c-neutral-btn ${
            isMobDate === false ? "c-selected-btn" : ""
          }`}
          size={_size.S}
          onClick={() => setIsMobDate(false)}
        >
          Дата
        </Button>
        <Button
          className={`c-neutral-btn ${
            isMobDate === true ? "c-selected-btn" : ""
          }`}
          size={_size.S}
          onClick={() => setIsMobDate(true)}
        >
          Время
        </Button>
      </div>
    </div>
  );
};

export default Calendar;
